import { useState, useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import type { TFunction } from 'i18next';

type UseSessionDeleteParams = {
  configRefetch: () => void;
  t: TFunction;
};

type UseSessionDeleteReturn = {
  deleteInProgress: number | undefined;
  deleteConfiguration: (id: number) => void;
  confirmDelete: (id: number) => void;
};

export const useSessionDelete = ({
  configRefetch,
  t,
}: UseSessionDeleteParams): UseSessionDeleteReturn => {
  const [deleteInProgress, setDeleteInProgress] = useState<number>();

  const deleteConfiguration = useCallback(
    (id: number) => {
      setDeleteInProgress(id);
      fetch(`${import.meta.env.VITE_API_URL}/services/configurations/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })
        .then(response => {
          if (!response.ok) {
            return response.text().then(text => {
              throw new Error(`HTTP ${response.status}: ${text}`);
            });
          }
        })
        .then(() => {
          configRefetch();
          notifications.show({
            color: 'green',
            title: 'Successo',
            message: 'Configurazione eliminata con successo',
          });
        })
        .catch(error => {
          notifications.show({
            color: 'red',
            withBorder: true,
            radius: 'lg',
            title: 'An error occurred',
            autoClose: false,
            message: error.message,
          });
        })
        .finally(() => {
          setDeleteInProgress(undefined);
        });
    },
    [configRefetch]
  );

  const confirmDelete = useCallback(
    (id: number) =>
      modals.openConfirmModal({
        title: t(
          'pa_confirm_delete',
          'Confermi di voler cancellare questa configurazione?'
        ),
        children:
          'Se hai intenzione di cancellare la configurazione selezionata clicca conferma, altrimenti annulla per tornare indietro.',
        labels: {
          confirm: t('confirm', 'Conferma'),
          cancel: t('cancel', 'Annulla'),
        },
        confirmProps: { color: 'red' },
        onCancel: () => {},
        onConfirm: () => deleteConfiguration(id),
      }),
    [t, deleteConfiguration]
  );

  return {
    deleteInProgress,
    deleteConfiguration,
    confirmDelete,
  };
};
